import React, { useState, useEffect } from 'react';
import {
  Card, Row, Col, Input, Select, Button, Tag, Image, Typography, Space,
  message, Modal, Spin, Empty,
  Pagination
} from 'antd';
import {
  EyeOutlined,
  GlobalOutlined
} from '@ant-design/icons';
import axios from '../../utils/axios';
import dayjs from 'dayjs';
import {
  normalizeTermsForDisplay,
  normalizeDescriptionForDisplay,
  hasVisibleHtmlContent,
} from '../../utils/termsAndConditionsDisplay';
import { responsiveFilterSelectProps } from '../../utils/responsiveFilterSelect';
import '../../styles/richtext.css';

const { Option } = Select;
const { Title, Text } = Typography;

// Category color mapping for better visual distinction
const categoryColors = {
  'food_beverage': { color: '#52c41a', bg: 'rgba(82, 196, 26, 0.9)' },
  'health': { color: '#ff7a45', bg: 'rgba(255, 122, 69, 0.9)' },
  'leisure_entertainment': { color: '#eb2f96', bg: 'rgba(235, 47, 150, 0.9)' },
  'lifestyle_services': { color: '#722ed1', bg: 'rgba(114, 46, 209, 0.9)' },
  'travel_hospitality': { color: '#13c2c2', bg: 'rgba(19, 194, 194, 0.9)' },
  'wellness': { color: '#f759ab', bg: 'rgba(247, 89, 171, 0.9)' },
  'others': { color: '#8c8c8c', bg: 'rgba(140, 140, 140, 0.9)' }
};

// Helper function to get category style
const getCategoryStyle = (category) => {
  const categoryKey = category?.toLowerCase();
  const categoryStyle = categoryColors[categoryKey] || categoryColors['others'];
  return {
    margin: 0,
    fontWeight: '600',
    fontSize: '10px',
    letterSpacing: '0.5px',
    background: categoryStyle.bg,
    color: 'white',
    border: 'none',
    borderRadius: '14px',
    padding: '3px 10px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    transition: 'all 0.2s ease-in-out',
    cursor: 'default',
    userSelect: 'none'
  };
};

const VoucherCatalog = () => {
  // State
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedVoucher, setSelectedVoucher] = useState(null);
  const [categories, setCategories] = useState([]);
  const [countries, setCountries] = useState([]);
  const [brands, setBrands] = useState([]);
  
  // Filters
  const [filters, setFilters] = useState({
    search: '',
    category: [], // Changed to array for multi-select
    country: [], // Changed to array for multi-select
    brand: [], // Changed to array for multi-select
    minValue: '',
    maxValue: '',
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });

  // Temporary filters for Apply functionality (like admin)
  const [tempFilters, setTempFilters] = useState({
    category: [],
    country: [],
    brand: []
  });
  
  // Pagination
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 12,
    total: 0
  });

  // Fetch vouchers
  const fetchVouchers = async () => {
    try {
      setLoading(true);
      
      // Filter out empty values from filters
      const cleanFilters = Object.entries(filters).reduce((acc, [key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          acc[key] = value;
        }
        return acc;
      }, {});
      
      const params = {
        page: pagination.current,
        limit: pagination.pageSize,
        ...cleanFilters
      };

      const response = await axios.get('/vouchers', { params });
      
      
      setVouchers(
        (response.data.data?.vouchers || []).filter(
          // Internal vouchers don't carry a `providerName`; previously they
          // were silently filtered out, leaving them invisible to clients.
          // Only require providerName for external/provider-backed vouchers.
          (v) =>
            v.isActive !== false &&
            v.voucherStatus === "online" &&
            (!v.isExternalVoucher || v.providerName),
        ),
      );
      setPagination(prev => ({
        ...prev,
        total: response.data.data?.pagination?.totalCount || 0
      }));
    } catch (error) {
      message.error('Failed to load vouchers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVouchers();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch on page/filter change only
  }, [pagination.current, filters]);

  // Separate useEffect for filter options - only run once on mount
  useEffect(() => {
    fetchFilterOptions();
  }, []);

  // Initialize temp filters when component mounts or filters change
  useEffect(() => {
    setTempFilters({
      category: filters.category || [],
      country: filters.country || [],
      brand: filters.brand || []
    });
  }, [filters.category, filters.country, filters.brand]);

  // Fetch filter options
  const fetchFilterOptions = async () => {
    try {
      const [categoriesRes, countriesRes, brandsRes] = await Promise.all([
        axios.get('/vouchers/categories'),
        axios.get('/vouchers/countries'),
        axios.get('/vouchers/brands')
      ]);
      
      setCategories(categoriesRes.data.data || []);
      setCountries(countriesRes.data.data || []);
      setBrands(brandsRes.data.data || []);
    } catch (error) {
    }
  };

  // Handle filter change
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // Handle apply category filters
  const handleApplyCategory = () => {
    setFilters(prev => ({
      ...prev,
      category: tempFilters.category.length > 0 ? tempFilters.category : []
    }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // Handle apply country filters
  const handleApplyCountry = () => {
    setFilters(prev => ({
      ...prev,
      country: tempFilters.country.length > 0 ? tempFilters.country : []
    }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // Handle apply brand filters
  const handleApplyBrand = () => {
    setFilters(prev => ({
      ...prev,
      brand: tempFilters.brand.length > 0 ? tempFilters.brand : []
    }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };
  
  // Reset filters
  const resetFilters = () => {
    setFilters({
      search: '',
      category: [], // Reset to empty array
      country: [], // Reset to empty array
      brand: [], // Reset to empty array
      minValue: '',
      maxValue: '',
      sortBy: 'createdAt',
      sortOrder: 'desc'
    });
    // Also reset temp filters
    setTempFilters({
      category: [],
      country: [],
      brand: []
    });
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // Voucher card component
  const VoucherCard = ({ voucher }) => {
    return (
      <Card
        hoverable
        className="voucher-card"
        size="small"
        style={{
          borderRadius: 12,
          overflow: 'hidden',
          boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
          transition: 'all 0.3s ease',
          border: '1px solid #f0f0f0',
          cursor: 'pointer'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-4px)';
          e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)';
        }}
        onClick={() => {
          setSelectedVoucher(voucher);
          setDetailModalVisible(true);
        }}
        bodyStyle={{ padding: '10px' }} // Reduced from 12px to 10px
        cover={
          <div style={{ position: 'relative', height: 160, overflow: 'hidden' }}>
            <Image
              alt={voucher.name}
              src={voucher.images?.[0]}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              preview={false}
              fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN"
            />
            {/* Optimized category tag - top left */}
            <div style={{ 
              position: 'absolute', 
              top: 8, 
              left: 8,
              zIndex: 2
            }}>
              <Tag style={getCategoryStyle(voucher.category)}>
                {voucher.category?.replace('_', ' ').toUpperCase()}
              </Tag>
            </div>
          </div>
        }
        actions={[
          <div key="actions" style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            width: '100%',
            padding: '4px 12px'
          }}>
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={(e) => {
                e.stopPropagation(); // Prevent card click handler from firing
                setSelectedVoucher(voucher);
                setDetailModalVisible(true);
              }}
              style={{ 
                fontSize: '12px', 
                color: '#666',
                border: 'none',
                padding: '6px 10px',
                height: 'auto',
                borderRadius: '6px',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#1890ff';
                e.currentTarget.style.background = '#f0f9ff';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = '#666';
                e.currentTarget.style.background = 'transparent';
              }}
            >
              Details
            </Button>
          </div>
        ]}
      >
        <Card.Meta
          title={
            <div style={{ marginBottom: 4 }}> {/* Reduced from 6px to 4px */}
              {/* Voucher name - supports line wrap */}
              <div style={{ 
                fontSize: '15px', 
                fontWeight: '600', 
                color: '#262626',
                lineHeight: '1.3',
                marginBottom: '4px', // Reduced from 6px to 4px
                minHeight: '38px',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden'
              }}>
                {voucher.name}
              </div>
              
              {/* Brand, Provider and Price */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                flexWrap: 'wrap',
                rowGap: 4
              }}>
                <div style={{ flex: 1, minWidth: 0, paddingRight: 8 }}>
                  <div style={{ 
                    fontSize: '12px', 
                    color: '#8c8c8c',
                    fontWeight: '400',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {voucher.brand}
                  </div>
                  {voucher.providerName && (
                    <div style={{ 
                      fontSize: '11px', 
                      color: '#b0b0b0',
                      fontWeight: '400',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {voucher.providerName}
                    </div>
                  )}
                </div>
                <div style={{ 
                  fontSize: '16px',
                  fontWeight: '700',
                  color: '#1890ff',
                  flexShrink: 0,
                  marginLeft: 'auto',
                  whiteSpace: 'nowrap',
                  lineHeight: 1.25
                }}>
                  {voucher.clientPrice && voucher.clientCurrency ? 
                    `${voucher.clientCurrency} ${voucher.clientPrice.toLocaleString()}` :
                    `${voucher.currency || voucher.originalCurrency || 'USD'} ${(voucher.basePrice || voucher.price || voucher.originalPrice || 0).toLocaleString()}`
                  }
                </div>
              </div>
            </div>
          }
          description={
            <div>
              {/* Clean info section with reduced spacing */}
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: 4, // Reduced from 6px to 4px
                marginBottom: 6 // Reduced from 8px to 6px
              }}>
                {/* Country and Stock - first group - removed border */}
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  padding: '2px 0' // Reduced from 4px to 2px
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <GlobalOutlined style={{ color: '#52c41a', fontSize: '12px' }} />
                    <Text style={{ fontSize: '12px', color: '#666' }}>{voucher.country}</Text>
                  </div>
                  <Text style={{
                    fontSize: '12px',
                    color: (voucher.availableQuantity || 0) < 10 ? '#ff4d4f' : '#52c41a',
                    fontWeight: '500'
                  }}>
                    {`${voucher.availableQuantity || 0} left`}
                  </Text>
                </div>
                
                {/* Min Order and Validity - second group */}
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  padding: '2px 0' // Reduced from 4px to 2px
                }}>
                  <div>
                    <Text style={{ fontSize: '12px', color: '#666' }}>
                      Min: {voucher.minimumOrderQuantity || 1}
                    </Text>
                  </div>
                  <Text style={{ 
                    fontSize: '12px', 
                    color: '#ff7a45',
                    fontWeight: '500'
                  }}>
                    {voucher.expiryEndDatetime
                      ? `Until ${dayjs(voucher.expiryEndDatetime).format('MMM DD, YYYY')}`
                      : 'No expiry date'}
                  </Text>
                </div>
              </div>

              {/* Tags - compact */}
              {(voucher.isPopular || voucher.isLowStock) && (
                <div style={{ marginTop: 2 }}> {/* Reduced from 4px to 2px */}
                  {voucher.isPopular && (
                    <Tag 
                      color="green" 
                      size="small" 
                      style={{ 
                        borderRadius: '10px',
                        fontSize: '10px',
                        marginRight: 4,
                        padding: '1px 6px'
                      }}
                    >
                      Popular
                    </Tag>
                  )}
                  {voucher.isLowStock && (
                    <Tag 
                      color="orange" 
                      size="small"
                      style={{ 
                        borderRadius: '10px',
                        fontSize: '10px',
                        padding: '1px 6px'
                      }}
                    >
                      Low Stock
                    </Tag>
                  )}
                </div>
              )}
            </div>
          }
        />
      </Card>
    );
  };

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>Voucher Catalog</Title>
        <Text type="secondary">
          Browse and purchase vouchers
        </Text>
      </div>

      {/* Filters */}
      {/* Filter area: no Card wrapper, left-align main content */}
      <div className="responsive-filter-card" style={{ marginBottom: 16, background: '#fff', padding: '16px 0 16px 0', boxShadow: 'none', border: 'none', borderRadius: 0, width: '100%' }}>
        <Row className="responsive-filter-row" gutter={[16, 12]} style={{ marginLeft: 0, marginRight: 0, width: '100%' }}>
          {/* Search */}
          <Col xs={24} sm={12} md={8} lg={6} xl={5}>
            <div className="responsive-filter-field" data-filter-label="Search">
              <Input
                placeholder="Search vouchers..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                allowClear
                style={{ borderRadius: '6px' }}
              />
            </div>
          </Col>
          
          {/* Category Filter */}
          <Col xs={24} sm={12} md={8} lg={6} xl={4}>
            <div className="responsive-filter-field" data-filter-label="Category">
              <Select
                mode="multiple"
                placeholder="Category"
                value={tempFilters.category}
                onChange={(value) => setTempFilters(prev => ({ ...prev, category: value }))}
                allowClear
                {...responsiveFilterSelectProps}
                style={{ width: '100%', borderRadius: '6px' }}
                maxTagCount="responsive"
                menuItemSelectedIcon={null}
              dropdownRender={(menu) => (
                <div>
                  {menu}
                  <div style={{ 
                    padding: '12px', 
                    borderTop: '1px solid #f0f0f0',
                    background: '#fafafa',
                    display: 'flex',
                    justifyContent: 'center'
                  }}>
                    <Button
                      type="primary"
                      size="small"
                      onClick={() => {
                        handleApplyCategory();
                        document.activeElement.blur();
                      }}
                      disabled={tempFilters.category.length === 0}
                      style={{ 
                        minWidth: '100px',
                        borderRadius: '4px'
                      }}
                    >
                      Apply {tempFilters.category.length > 0 && `(${tempFilters.category.length})`}
                    </Button>
                  </div>
                </div>
              )}
              optionLabelProp="label"
            >
              {categories.map(cat => (
                <Option key={cat} value={cat} label={cat.charAt(0).toUpperCase() + cat.slice(1).replace('_', ' ')}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    padding: '8px 8px',
                    minHeight: '32px'
                  }}>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      minWidth: '16px',
                      border: '2px solid #d9d9d9',
                      borderRadius: '3px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: tempFilters.category.includes(cat) ? '#1890ff' : 'transparent',
                      borderColor: tempFilters.category.includes(cat) ? '#1890ff' : '#d9d9d9',
                      transition: 'all 0.2s ease',
                      marginRight: '12px'
                    }}>
                      {tempFilters.category.includes(cat) && (
                        <span style={{ color: 'white', fontSize: '10px', fontWeight: 'bold' }}>✓</span>
                      )}
                    </div>
                    <span style={{ 
                      fontSize: '14px',
                      color: tempFilters.category.includes(cat) ? '#1890ff' : '#333',
                      flex: 1,
                      lineHeight: '1.4',
                      wordBreak: 'break-word'
                    }}>
                      {cat.charAt(0).toUpperCase() + cat.slice(1).replace('_', ' ')}
                    </span>
                  </div>
                </Option>
              ))}
              </Select>
            </div>
          </Col>
          
          {/* Country Filter */}
          <Col xs={24} sm={12} md={8} lg={6} xl={4}>
            <div className="responsive-filter-field" data-filter-label="Country">
              <Select
                mode="multiple"
                placeholder="Country"
                value={tempFilters.country}
                onChange={(value) => setTempFilters(prev => ({ ...prev, country: value }))}
                allowClear
                {...responsiveFilterSelectProps}
                style={{ width: '100%', borderRadius: '6px' }}
                maxTagCount="responsive"
                showSearch
                menuItemSelectedIcon={null}
                filterOption={(input, option) =>
                  option.children.props.children[1].props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              dropdownRender={(menu) => (
                <div>
                  {menu}
                  <div style={{ 
                    padding: '12px', 
                    borderTop: '1px solid #f0f0f0',
                    background: '#fafafa',
                    display: 'flex',
                    justifyContent: 'center'
                  }}>
                    <Button
                      type="primary"
                      size="small"
                      onClick={() => {
                        handleApplyCountry();
                        document.activeElement.blur();
                      }}
                      disabled={tempFilters.country.length === 0}
                      style={{ 
                        minWidth: '100px',
                        borderRadius: '4px'
                      }}
                    >
                      Apply {tempFilters.country.length > 0 && `(${tempFilters.country.length})`}
                    </Button>
                  </div>
                </div>
              )}
              optionLabelProp="label"
            >
              {countries.map(country => (
                <Option key={country} value={country} label={country}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    padding: '8px 8px',
                    minHeight: '32px'
                  }}>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      minWidth: '16px',
                      border: '2px solid #d9d9d9',
                      borderRadius: '3px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: tempFilters.country.includes(country) ? '#1890ff' : 'transparent',
                      borderColor: tempFilters.country.includes(country) ? '#1890ff' : '#d9d9d9',
                      transition: 'all 0.2s ease',
                      marginRight: '12px'
                    }}>
                      {tempFilters.country.includes(country) && (
                        <span style={{ color: 'white', fontSize: '10px', fontWeight: 'bold' }}>✓</span>
                      )}
                    </div>
                    <span style={{ 
                      fontSize: '14px',
                      color: tempFilters.country.includes(country) ? '#1890ff' : '#333',
                      flex: 1,
                      lineHeight: '1.4',
                      wordBreak: 'break-word'
                    }}>
                      {country}
                    </span>
                  </div>
                </Option>
              ))}
              </Select>
            </div>
          </Col>

          {/* Brand Filter */}
          <Col xs={24} sm={12} md={8} lg={6} xl={4}>
            <div className="responsive-filter-field" data-filter-label="Brand">
              <Select
                mode="multiple"
                placeholder="Brand"
                value={tempFilters.brand}
                onChange={(value) => setTempFilters(prev => ({ ...prev, brand: value }))}
                allowClear
                {...responsiveFilterSelectProps}
                style={{ width: '100%', borderRadius: '6px' }}
                maxTagCount="responsive"
                showSearch
                menuItemSelectedIcon={null}
                filterOption={(input, option) =>
                  option.children.props.children[1].props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              dropdownRender={(menu) => (
                <div>
                  {menu}
                  <div style={{ 
                    padding: '12px', 
                    borderTop: '1px solid #f0f0f0',
                    background: '#fafafa',
                    display: 'flex',
                    justifyContent: 'center'
                  }}>
                    <Button
                      type="primary"
                      size="small"
                      onClick={() => {
                        handleApplyBrand();
                        document.activeElement.blur();
                      }}
                      disabled={tempFilters.brand.length === 0}
                      style={{ 
                        minWidth: '100px',
                        borderRadius: '4px'
                      }}
                    >
                      Apply {tempFilters.brand.length > 0 && `(${tempFilters.brand.length})`}
                    </Button>
                  </div>
                </div>
              )}
              optionLabelProp="label"
            >
              {brands.map(brand => (
                <Option key={brand} value={brand} label={brand}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    padding: '8px 8px',
                    minHeight: '32px'
                  }}>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      minWidth: '16px',
                      border: '2px solid #d9d9d9',
                      borderRadius: '3px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: tempFilters.brand.includes(brand) ? '#1890ff' : 'transparent',
                      borderColor: tempFilters.brand.includes(brand) ? '#1890ff' : '#d9d9d9',
                      transition: 'all 0.2s ease',
                      marginRight: '12px'
                    }}>
                      {tempFilters.brand.includes(brand) && (
                        <span style={{ color: 'white', fontSize: '10px', fontWeight: 'bold' }}>✓</span>
                      )}
                    </div>
                    <span style={{ 
                      fontSize: '14px',
                      color: tempFilters.brand.includes(brand) ? '#1890ff' : '#333',
                      flex: 1,
                      lineHeight: '1.4',
                      wordBreak: 'break-word'
                    }}>
                      {brand}
                    </span>
                  </div>
                </Option>
              ))}
              </Select>
            </div>
          </Col>
          
          {/* Sort */}
          <Col xs={24} sm={12} md={8} lg={6} xl={4}>
            <div className="responsive-filter-field" data-filter-label="Sort">
              <Select
                placeholder="Sort by"
                value={`${filters.sortBy}-${filters.sortOrder}`}
                onChange={(value) => {
                  const [sortBy, sortOrder] = value.split('-');
                  handleFilterChange('sortBy', sortBy);
                  handleFilterChange('sortOrder', sortOrder);
                }}
                {...responsiveFilterSelectProps}
                style={{ width: '100%', borderRadius: '6px' }}
              >
                <Option value="createdAt-desc">Newest First</Option>
                <Option value="createdAt-asc">Oldest First</Option>
                <Option value="normalizedPrice-asc">Price Low-High</Option>
                <Option value="normalizedPrice-desc">Price High-Low</Option>
              </Select>
            </div>
          </Col>
          
          {/* Action Buttons */}
          <Col xs={24} sm={12} md={8} lg={6} xl={3}>
            <div className="responsive-filter-actions" style={{ paddingTop: 18 }}>
              <Button 
                onClick={resetFilters} 
                style={{ 
                  width: '100%',
                  borderRadius: '6px'
                }}
                size="middle"
              >
                Reset
              </Button>
            </div>
          </Col>
        </Row>
        
        {/* Active Filters Display */}
        {(filters.search || filters.category?.length > 0 || filters.country?.length > 0 || filters.brand?.length > 0) && (
          <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #f0f0f0' }}>
            <Text type="secondary" style={{ marginRight: '8px' }}>Active filters:</Text>
            <Space wrap>
              {filters.search && (
                <Tag 
                  closable 
                  onClose={() => handleFilterChange('search', '')}
                  color="blue"
                >
                  Search: {filters.search}
                </Tag>
              )}
              {filters.category?.length > 0 && filters.category.map(category => (
                <Tag 
                  key={category}
                  closable 
                  onClose={() => {
                    const newCategories = filters.category.filter(c => c !== category);
                    handleFilterChange('category', newCategories);
                  }}
                  color="green"
                >
                  Category: {category.charAt(0).toUpperCase() + category.slice(1).replace('_', ' ')}
                </Tag>
              ))}
              {filters.country?.length > 0 && filters.country.map(country => (
                <Tag 
                  key={country}
                  closable 
                  onClose={() => {
                    const newCountries = filters.country.filter(c => c !== country);
                    handleFilterChange('country', newCountries);
                  }}
                  color="orange"
                >
                  Country: {country}
                </Tag>
              ))}
              {filters.brand?.length > 0 && filters.brand.map(brand => (
                <Tag 
                  key={brand}
                  closable 
                  onClose={() => {
                    const newBrands = filters.brand.filter(b => b !== brand);
                    handleFilterChange('brand', newBrands);
                  }}
                  color="purple"
                >
                  Brand: {brand}
                </Tag>
              ))}
            </Space>
          </div>
        )}
      </div>

      {/* Voucher Grid */}
      <Spin spinning={loading}>
        {vouchers.length === 0 && !loading ? (
          <Empty description="No vouchers found" />
        ) : (
          <>
            <Row gutter={[16, 16]}>
              {vouchers.map(voucher => (
                <Col xs={24} sm={12} md={8} lg={6} key={voucher._id}>
                  <VoucherCard voucher={voucher} />
                </Col>
              ))}
            </Row>

            {/* Pagination */}
            <div style={{ textAlign: 'center', marginTop: '32px' }}>
              <Pagination
                current={pagination.current}
                pageSize={pagination.pageSize}
                total={pagination.total}
                showQuickJumper
                showTotal={(total, range) =>
                  `${range[0]}-${range[1]} of ${total} vouchers`
                }
                onChange={(page) => {
                  setPagination(prev => ({ ...prev, current: page }));
                }}
              />
            </div>
          </>
        )}
      </Spin>

      {/* Voucher Detail Modal */}
      <Modal
        title={null}
        open={detailModalVisible}
        onCancel={() => {
          setDetailModalVisible(false);
          setSelectedVoucher(null);
        }}
        footer={[
          <Button
            key="close"
            type="primary"
            onClick={() => {
              setDetailModalVisible(false);
              setSelectedVoucher(null);
            }}
          >
            Close
          </Button>
        ]}
        width={900}
        centered
        bodyStyle={{ padding: '24px' }}
        style={{ top: 20 }}
      >
        {selectedVoucher && (
          <div>
            {/* Clean header with voucher name and category */}
            <div style={{ 
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: '32px',
              paddingBottom: '20px',
              borderBottom: '2px solid #f0f0f0'
            }}>
              <div style={{ flex: 1, marginRight: '16px' }}>
                <Title level={2} style={{ margin: 0, color: '#262626', marginBottom: '8px' }}>
                  {selectedVoucher.name}
                </Title>
                <Text style={{ color: '#8c8c8c', fontSize: '16px' }}>
                  by {selectedVoucher.brand}
                </Text>
                {selectedVoucher.providerName && (
                  <div>
                    <Text style={{ color: '#b0b0b0', fontSize: '14px' }}>
                      Provider: {selectedVoucher.providerName}
                    </Text>
                  </div>
                )}
              </div>
              <Tag style={getCategoryStyle(selectedVoucher.category)}>
                {selectedVoucher.category?.replace('_', ' ').toUpperCase()}
              </Tag>
            </div>
            
            <Row gutter={32}>
              {/* Left: Image */}
              <Col span={10}>
                <Image
                  width="100%"
                  src={selectedVoucher.images?.[0]}
                  style={{ borderRadius: '12px' }}
                  fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN"
                />
              </Col>
              
              {/* Right: Details */}
              <Col span={14}>
                {/* Price and Brand */}
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ 
                    fontSize: '32px', 
                    fontWeight: '700', 
                    color: '#1890ff',
                    marginBottom: '4px'
                  }}>
                    {/* Detail page: prefer tier price, keep original currency format */}
                    {selectedVoucher.clientPrice && selectedVoucher.clientCurrency ? 
                      `${selectedVoucher.clientCurrency} ${selectedVoucher.clientPrice.toLocaleString()}` :
                      `${selectedVoucher.currency || selectedVoucher.originalCurrency || 'USD'} ${(selectedVoucher.basePrice || selectedVoucher.price || selectedVoucher.originalPrice || 0).toLocaleString()}`
                  }
                  </div>
                  {/* If tier price differs from original currency, show original as reference */}
                  {selectedVoucher.clientPrice && selectedVoucher.clientCurrency && 
                   selectedVoucher.clientCurrency !== (selectedVoucher.currency || selectedVoucher.originalCurrency) && (
                    <Text style={{ fontSize: '14px', color: '#8c8c8c', marginBottom: '4px' }}>
                      Face Value: {selectedVoucher.currency || selectedVoucher.originalCurrency || 'USD'} {(selectedVoucher.basePrice || selectedVoucher.price || selectedVoucher.originalPrice || 0).toLocaleString()}
                    </Text>
                  )}
                </div>
                
                {/* Key Info in clean rows - optimized for better alignment */}
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    padding: '10px 0',
                    borderBottom: '1px solid #f5f5f5'
                  }}>
                    <Text style={{ color: '#8c8c8c', fontSize: '14px' }}>Region</Text>
                    <Text style={{ fontWeight: '500', color: '#262626', fontSize: '14px' }}>
                      <GlobalOutlined style={{ color: '#52c41a', marginRight: 6 }} />
                      {selectedVoucher.country}
                    </Text>
                  </div>
                  
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    padding: '10px 0',
                    borderBottom: '1px solid #f5f5f5'
                  }}>
                    <Text style={{ color: '#8c8c8c', fontSize: '14px' }}>Available Stock</Text>
                    <Text style={{
                      fontWeight: '500',
                      color: (selectedVoucher.availableQuantity || 0) < 10 ? '#ff4d4f' : '#52c41a',
                      fontSize: '14px'
                    }}>
                      {selectedVoucher.availableQuantity || 0}
                    </Text>
                  </div>
                  
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    padding: '10px 0',
                    borderBottom: '1px solid #f5f5f5'
                  }}>
                    <Text style={{ color: '#8c8c8c', fontSize: '14px' }}>Minimum Order</Text>
                    <Text style={{ fontWeight: '500', color: '#262626', fontSize: '14px' }}>
                      {selectedVoucher.minimumOrderQuantity || 1}
                    </Text>
                  </div>

                  {selectedVoucher.providerName === 'zhongmin' && (
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '10px 0',
                      borderBottom: '1px solid #f5f5f5'
                    }}>
                      <Text style={{ color: '#8c8c8c', fontSize: '14px' }}>Zhongmin type</Text>
                      {String(selectedVoucher.type || '').trim() ? (
                        <Text style={{ fontWeight: '500', color: '#262626', fontSize: '14px' }}>
                          {String(selectedVoucher.type).trim()}
                        </Text>
                      ) : (
                        <div
                          style={{
                            minWidth: 120,
                            height: 28,
                            background: '#f5f5f5',
                            border: '1px solid #d9d9d9',
                            borderRadius: 6,
                            boxSizing: 'border-box'
                          }}
                          aria-hidden
                        />
                      )}
                    </div>
                  )}
                  
                  {/* Validity Period */}
                  {(() => {
                    const now = dayjs();
                    const startDate = selectedVoucher.expiryStartDatetime
                      ? dayjs(selectedVoucher.expiryStartDatetime)
                      : null;
                    const endDate = selectedVoucher.expiryEndDatetime
                      ? dayjs(selectedVoucher.expiryEndDatetime)
                      : null;
                    const showValidFrom =
                      startDate && startDate.isValid() && startDate.isAfter(now);

                    return (
                      <>
                        {showValidFrom && (
                          <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            padding: '10px 0',
                            borderBottom: '1px solid #f5f5f5'
                          }}>
                            <Text style={{ color: '#8c8c8c', fontSize: '14px' }}>Valid From</Text>
                            <Text style={{ fontWeight: '500', color: '#1890ff', fontSize: '14px' }}>
                              {startDate.format('MMM DD, YYYY')}
                            </Text>
                          </div>
                        )}
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          padding: '10px 0',
                          borderBottom: '1px solid #f5f5f5'
                        }}>
                          <Text style={{ color: '#8c8c8c', fontSize: '14px' }}>Valid Until</Text>
                          <Text style={{ fontWeight: '500', color: '#ff7a45', fontSize: '14px' }}>
                            {endDate && endDate.isValid()
                              ? endDate.format('MMM DD, YYYY')
                              : 'No expiry date'}
                          </Text>
                        </div>
                      </>
                    );
                  })()}
                  
                  {/* Voucher Type */}
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    padding: '10px 0',
                    borderBottom: '1px solid #f5f5f5'
                  }}>
                    <Text style={{ color: '#8c8c8c', fontSize: '14px' }}>Voucher Type</Text>
                    <Text style={{ fontWeight: '500', color: '#262626', fontSize: '14px' }}>
                      {selectedVoucher.voucherType || 'Digital Voucher'}
                    </Text>
                  </div>
                  
                  {/* Delivery Method */}
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    padding: '10px 0'
                  }}>
                    <Text style={{ color: '#8c8c8c', fontSize: '14px' }}>Delivery Method</Text>
                    <Text style={{ fontWeight: '500', color: '#262626', fontSize: '14px' }}>
                      {(() => {
                        const consumptionType = selectedVoucher.consumptionType;
                        if (consumptionType === 'Coupon Code') return 'Digital Code';
                        if (consumptionType === 'QR Code') return 'QR Code';
                        if (consumptionType === 'URL') return 'Direct Link';
                        return 'Digital Delivery';
                      })()}
                    </Text>
                  </div>
                </div>
                
                {/* Tags */}
                {(selectedVoucher.isPopular || selectedVoucher.isLowStock || (selectedVoucher.tags && selectedVoucher.tags.length > 0)) && (
                  <div style={{ marginBottom: '20px' }}>
                    {selectedVoucher.isPopular && (
                      <Tag color="green" style={{ marginRight: 8, marginBottom: 8, borderRadius: '12px' }}>
                        🔥 Popular Choice
                      </Tag>
                    )}
                    {selectedVoucher.isLowStock && (
                      <Tag color="orange" style={{ marginRight: 8, marginBottom: 8, borderRadius: '12px' }}>
                        ⚡ Limited Stock
                      </Tag>
                    )}
                    {selectedVoucher.tags && selectedVoucher.tags.map((tag, index) => (
                      <Tag key={index} color="blue" style={{ marginRight: 8, marginBottom: 8, borderRadius: '12px' }}>
                        {tag}
                      </Tag>
                    ))}
                  </div>
                )}
              </Col>
            </Row>
            
            {/* Description Section */}
            <div style={{ marginTop: '32px' }}>
              <Title level={4} style={{ color: '#262626', marginBottom: '16px' }}>
                About This Voucher
              </Title>
              <div
                style={{
                  color: '#595959',
                  lineHeight: '1.7',
                  fontSize: '15px',
                  margin: 0,
                  background: '#fafafa',
                  padding: '20px',
                  borderRadius: '8px',
                  border: '1px solid #f0f0f0',
                }}
                dangerouslySetInnerHTML={{
                  __html: (() => {
                    const html = normalizeDescriptionForDisplay(
                      selectedVoucher.description,
                    );
                    return hasVisibleHtmlContent(html) ? html : '';
                  })(),
                }}
                className="voucher-rich-content voucher-rich-text-line-breaks"
              />
            </div>

            {/* Terms & Conditions */}
            <div style={{ marginTop: '32px' }}>
              <Title level={4} style={{ color: '#262626', marginBottom: '16px' }}>
                Terms & Conditions
              </Title>
              <div
                style={{
                  color: '#595959',
                  fontSize: '14px',
                  lineHeight: '1.6',
                  background: '#fafafa',
                  padding: '20px',
                  borderRadius: '8px',
                  border: '1px solid #f0f0f0',
                }}
                dangerouslySetInnerHTML={{
                  __html: (() => {
                    const html = normalizeTermsForDisplay(
                      selectedVoucher.termsAndConditions ??
                        selectedVoucher.usageTerms ??
                        '',
                    );
                    return hasVisibleHtmlContent(html) ? html : '';
                  })(),
                }}
                className="voucher-rich-content voucher-rich-text-line-breaks"
              />
            </div>
          </div>
        )}
      </Modal>

    </div>
  );
};

export default VoucherCatalog;
